import { api } from './client'

// ─── Tipos ────────────────────────────────────────────────────────────────────

/**
 * Recurso (ALB/CloudFront/API GW/AppSync/Cognito) protegido por um WAF, com os
 * domínios que passam por ele. Formato retornado pelo backend em
 * GET /v1/waf/managed/{id} (campo `resources`).
 */
export interface WafResource {
  resource_type: 'ALB' | 'CLOUDFRONT' | 'API_GATEWAY' | 'APPSYNC' | 'COGNITO_USER_POOL'
  resource_arn: string
  resource_name?: string
  resource_dns?: string
  domains: string[]
  domains_partial: boolean
  /**
   * Quando esta lista foi descoberta. Ela é CACHE (`managed_waf_resources`), não
   * estado ao vivo: em 12/08/2026 a tela exibia 4 domínios de um Web ACL já apagado
   * da AWS, com cache de 11/08. Cache sem data é afirmação sobre o presente.
   */
  discovered_at?: string | null
}

export interface WafMetricPoint {
  ts: string
  value: number
}

/**
 * Formato retornado pelo backend:
 * { waf_id, range_minutes, metrics: { [rule: string]: { [metric: string]: WafMetricPoint[] } } }
 */
export interface WafMetricsResponse {
  waf_id: number
  range_minutes: number
  metrics: Record<string, Record<string, WafMetricPoint[]>>
}

export interface WafSampledRequest {
  timestamp: string
  client_ip: string
  country: string
  uri: string
  method: string
  action: string
  rule: string
}

export interface WafSampledResponse {
  waf_id: number
  rule: string
  range_minutes: number
  samples: WafSampledRequest[]
}

export interface WafLogsSearchBody {
  query?: string
  range_minutes: number
  host?: string
}

export interface WafLogsSearchResponse {
  status: 'Complete' | 'Failed' | 'Cancelled' | 'Timeout' | 'unavailable'
  message?: string
  /**
   * Forma BRUTA do CloudWatch Logs Insights: cada linha é uma LISTA de
   * `{field, value}`. NÃO renderizar direto — foi o que deu `[object Object]` em
   * toda célula e cabeçalho `0,1,2` até 12/08/2026, porque este tipo dizia
   * `Record<string, unknown>` e o componente obedeceu ao tipo. Mantido porque a
   * forma bruta ainda serve consumidor server-side.
   */
  results?: Array<Array<{ field?: string; value?: string }>>
  /** Linhas normalizadas pelo backend. É isto que a tela renderiza. */
  rows?: Array<Record<string, string>>
  /**
   * Nome do grupo de log, presente só quando o log está LIGADO. É o que permite a
   * tela renderizar o estado "Log habilitado" do `WafLoggingCard` — que existia
   * escrito no componente e nunca aparecia, por falta deste campo — e oferecer o
   * desligamento sem sair do produto.
   */
  log_group?: string | null
}

export interface WafAlarm {
  name: string
  state: string
  metric: string
  threshold: number
  comparison: string
  period: number
}

export interface WafAlarmsResponse {
  waf_id: number
  alarms: WafAlarm[]
}

export interface CreateWafAlarmBody {
  rule: string
  metric: string
  threshold: number
  comparison: string
  period: number
  alarm_name?: string
  sns_email?: string
}

// ─── Tipos — Gestão de Regras ─────────────────────────────────────────────────

export type WafRuleKind = 'managed' | 'rate' | 'ip' | 'geo' | 'byte' | 'regex' | 'json' | 'allow' | 'other'

/**
 * Regra de um Web ACL, lida ao vivo do AWS (GetWebACL) pelo backend.
 * Formato retornado em GET /v1/waf/managed/{id}/rules (campo `rules`).
 */
export interface WafRule {
  name: string
  priority: number
  kind: WafRuleKind
  effective_action: 'count' | 'active'
  metric?: string
  description?: string
  scope_kind?: 'host' | 'global' | 'managed'
  scoped_hosts?: string[] | null
}

export interface WafRulesResponse {
  waf_id: number
  rules: WafRule[]
  /**
   * `webacl_absent_in_aws` quando o Web ACL não existe mais na AWS e a linha
   * sobreviveu em `managed_wafs`. Antes de 12/08/2026 esse caso subia como 500 e a
   * tela dizia só "Request failed with status code 500".
   */
  error?: string
  message?: string
}

export interface CreateWafRuleBody {
  kind: WafRuleKind
  params: Record<string, unknown>
  name: string
  priority?: number
  reason?: string
  /**
   * Quando preenchido, a regra é aplicada apenas a este domínio (host-scoping):
   * o backend embrulha o statement num AndStatement + match no header `Host`.
   * Vazio/undefined = aplica a todos os domínios deste WAF.
   */
  scope_host?: string
  /** Multi-host: tem precedência sobre scope_host. '*.x' = curinga. */
  scope_hosts?: string[]
}

export interface PromoteWafRuleResponse {
  status?: 'applied' | 'pending_gmud' | 'skipped'
  gmud_id?: number
  /** WAF do código (spec 2026-10-14 D6): o gesto virou pedido do Terraform. */
  pedido_id?: number
  stage?: string
}

export interface ManagedRuleGroup {
  Name?: string
  VendorName?: string
  Description?: string
  [key: string]: unknown
}

export interface ManagedRuleGroupsResponse {
  groups: ManagedRuleGroup[]
}

// ─── Tipos — Observação (Count 7d por regra + selo) ───────────────────────────

export type WafRuleRecommendation = 'active' | 'no_data' | 'safe' | 'review' | 'high'

export interface WafRuleObservation {
  counted: number
  blocked: number
  pct: number | null
  recommendation: WafRuleRecommendation
}

export interface WafRulesObservationResponse {
  waf_id: number
  window_days: number
  allowed_total: number
  rules: Record<string, WafRuleObservation>
}

// ─── Funções de API — Fase B Observabilidade ──────────────────────────────────

export function getWafMetrics(id: number, rangeMinutes = 60): Promise<WafMetricsResponse> {
  return api
    .get<WafMetricsResponse>(`/v1/waf/managed/${id}/metrics`, {
      params: { range_minutes: rangeMinutes },
    })
    .then(r => r.data)
}

export function getWafSampled(id: number, rule?: string, rangeMinutes = 60): Promise<WafSampledResponse> {
  return api
    .get<WafSampledResponse>(`/v1/waf/managed/${id}/sampled`, {
      params: {
        ...(rule ? { rule } : {}),
        range_minutes: rangeMinutes,
      },
    })
    .then(r => r.data)
}

export function searchWafLogs(id: number, body: WafLogsSearchBody): Promise<WafLogsSearchResponse> {
  return api
    .post<WafLogsSearchResponse>(`/v1/waf/managed/${id}/logs/search`, body)
    .then(r => r.data)
}

/**
 * Resposta de ligar/desligar log. `blocked` NÃO é erro: a conta não tem as
 * permissões de log, e `missing` diz quais — é isso que o operador leva a quem
 * administra a conta AWS. "Sem permissão" sozinho não é acionável.
 */
export interface WafLoggingResponse {
  waf_id: number
  status: 'enabled' | 'disabled' | 'blocked'
  grupo?: string
  filtro?: 'match' | 'completo'
  retencao_dias?: number | null
  missing?: string[]
  motivo?: string
}

/**
 * Liga o log de tráfego da Web ACL.
 *
 * Desligado por omissão de propósito: log de WAF custa por GB ingerido, e a escolha
 * é do operador. `completo` grava toda requisição (investigação forense); o default
 * mantém apenas o que casou regra, que é o que o Logs Insights consulta.
 */
export function enableWafLogging(id: number, completo = false): Promise<WafLoggingResponse> {
  return api
    .post<WafLoggingResponse>(`/v1/waf/managed/${id}/logging`, null, {
      params: completo ? { completo: true } : {},
    })
    .then(r => r.data)
}

/** Desliga a entrega e PRESERVA o grupo de log — o dado já pago segue consultável. */
export function disableWafLogging(id: number): Promise<WafLoggingResponse> {
  return api
    .delete<WafLoggingResponse>(`/v1/waf/managed/${id}/logging`)
    .then(r => r.data)
}

export function listWafAlarms(id: number): Promise<WafAlarmsResponse> {
  return api
    .get<WafAlarmsResponse>(`/v1/waf/managed/${id}/alarms`)
    .then(r => r.data)
}

export function createWafAlarm(id: number, body: CreateWafAlarmBody): Promise<WafAlarm> {
  return api
    .post<WafAlarm>(`/v1/waf/managed/${id}/alarms`, body)
    .then(r => r.data)
}

export function deleteWafAlarm(id: number, alarmName: string): Promise<void> {
  return api
    .delete(`/v1/waf/managed/${id}/alarms/${encodeURIComponent(alarmName)}`)
    .then(() => undefined)
}

// ─── Funções de API — Gestão de Regras ────────────────────────────────────────

export function listWafRules(wafId: number): Promise<WafRulesResponse> {
  return api
    .get<WafRulesResponse>(`/v1/waf/managed/${wafId}/rules`)
    .then(r => r.data)
}

export function createWafRule(wafId: number, body: CreateWafRuleBody): Promise<unknown> {
  return api
    .post(`/v1/waf/managed/${wafId}/rules`, body)
    .then(r => r.data)
}

export function deleteWafRule(wafId: number, name: string): Promise<void> {
  return api
    .delete(`/v1/waf/managed/${wafId}/rules/${encodeURIComponent(name)}`)
    .then(() => undefined)
}

export function reorderWafRules(wafId: number, order: string[]): Promise<unknown> {
  return api
    .post(`/v1/waf/managed/${wafId}/rules/reorder`, { order })
    .then(r => r.data)
}

export function promoteWafRule(
  wafId: number,
  name: string,
  ruleKind: WafRuleKind,
): Promise<PromoteWafRuleResponse> {
  return api
    .post<PromoteWafRuleResponse>(
      `/v1/waf/managed/${wafId}/rules/${encodeURIComponent(name)}/promote`,
      { rule_kind: ruleKind },
    )
    .then(r => r.data)
}

export function demoteWafRule(
  wafId: number,
  name: string,
  ruleKind: WafRuleKind,
): Promise<PromoteWafRuleResponse> {
  return api
    .post<PromoteWafRuleResponse>(
      `/v1/waf/managed/${wafId}/rules/${encodeURIComponent(name)}/demote`,
      { rule_kind: ruleKind },
    )
    .then(r => r.data)
}

export function listManagedRuleGroups(wafId: number): Promise<ManagedRuleGroupsResponse> {
  return api
    .get<ManagedRuleGroupsResponse>(`/v1/waf/managed/${wafId}/managed-rule-groups`)
    .then(r => r.data)
}

// ─── Provisionamento (#3) — criar WebACL novo e anexar a um ALB ────────────────

export interface AttachableAlb {
  arn: string
  name: string
  dns_name: string
  scheme: string
  has_waf: boolean
  web_acl_name: string | null
  partial: boolean
}

export function listAttachableAlbs(accountId: string, region: string): Promise<{ albs: AttachableAlb[] }> {
  return api
    .get<{ albs: AttachableAlb[] }>('/v1/waf/provision/albs', {
      params: { account_id: accountId, region },
    })
    .then(r => r.data)
}

export interface ProvisionWafBody {
  tenant_id: number
  account_id: string
  region: string
  alb_arn: string
  alb_name?: string | null
  desired_name: string
  reason?: string | null
  /** Pedido falho que está sendo substituído pelo "tentar de novo". Faz o
   *  antigo virar `cancelled`, para a tela não mostrar dois cartões
   *  contraditórios sobre o mesmo ALB. */
  replace_request_id?: number
}

export interface ProvisionWafResponse {
  status: string
  request_id: number
  /** `null` quando o gate liberou: a organização não tem comitê de mudança,
   *  ou o recurso não é de produção. Ver `sem_gmud_porque`. */
  gmud_id: number | null
  /** Motivo pelo qual não exigiu GMUD, quando `gmud_id` é null. */
  sem_gmud_porque?: string
  /** Preenchido quando `status === 'blocked'`: quais ações faltam na role.
   *
   *  Vem no POST porque o modal DESLIGA o poll do pedido quando ele já nasce
   *  bloqueado (`enabled: statusInicial !== 'blocked'`) — sem este campo o
   *  motivo real nunca chega à tela e ela mostra o texto genérico para sempre. */
  error_detail?: string
}

// ─── Pedidos de provisionamento (09/08/2026) ──────────────────────────────────
//
// Desde o !1085, numa organização sem comitê o provisionamento acontece direto e
// em segundo plano. Sem esta leitura, uma falha da AWS ficava invisível: o
// operador recebia 200 e acreditava que o ALB estava protegido.

export type ProvisioningStatus =
  | 'pending'        // em andamento (ou tentando, se houver error_detail)
  | 'pending_gmud'   // esperando aprovação
  | 'failed'
  | 'cancelled'      // GMUD negada, ou substituído por nova tentativa
  // A role do ConsoleAI na conta do tenant não tem escrita de WAF. O pedido
  // existe, mas a AWS nunca foi chamada — quem resolve é quem administra a conta.
  | 'blocked'
  // TASK-10369/10370: terminal e IRREVERSÍVEL. O ConsoleAI perdeu o acesso à
  // conta, destravou o registro e o apagou — mas o Web ACL FICOU na AWS, e segue
  // cobrando. Não é retentável: a linha de `managed_wafs` já não existe.
  | 'orphaned'

export interface ProvisioningStep {
  id: number
  request_id: number
  /** Chave estável do marco: 'pedido_registrado' | 'webacl_criado' |
   *  'webacl_adotado' | 'aguardando_propagacao' | 'associado' | 'registrado' |
   *  'falhou' | 'cancelado'. */
  step: string
  status: 'ok' | 'wait' | 'error'
  /** Linguagem de operação — é o que vai para a tela. */
  message: string
  /** ARN, erro cru da AWS. Fica atrás de "ver detalhes". */
  detail: string | null
  attempts: number
  created_at: string
  updated_at: string
}

export interface ProvisioningRequest {
  id: number
  /** `deletion` só existe desde a MIGRATION_226; pedido antigo não tem `kind`.
   *  A tela PRECISA dele: sem ele, uma exclusão falha oferecia um botão que
   *  provisionava — criando na AWS o que se pediu para apagar. */
  kind?: 'provision' | 'deletion'
  desired_name: string
  target_alb_arn: string
  target_alb_name: string | null
  account_id: string
  region: string
  reason: string | null
  status: ProvisioningStatus
  /** Motivo da falha, vindo da AWS. Vai para a TELA — é ele que diz se o
   *  operador resolve sozinho (permissão, cota, ALB ocupado) ou se é problema
   *  da plataforma. */
  error_detail: string | null
  gmud_id: number | null
  created_at: string
  /** ARN do Web ACL que este pedido já criou, se criou. É ele que torna um Web
   *  ACL órfão endereçável quando o pedido falha depois da criação. */
  created_web_acl_arn: string | null
  /** O que o pedido já conseguiu fazer, em ordem cronológica. */
  steps: ProvisioningStep[]
}

export function listProvisioningRequests(
  tenantId: number,
): Promise<{ items: ProvisioningRequest[] }> {
  return api
    .get<{ items: ProvisioningRequest[] }>(
      `/v1/tenants/${tenantId}/waf/provisioning-requests`,
    )
    .then(r => r.data)
}

/**
 * Um pedido por id, em QUALQUER status — inclusive `applied`.
 *
 * É o que permite ao modal de progresso ver o sucesso ACONTECER. A lista
 * (`listProvisioningRequests`) exclui `applied` de propósito, então poliando ela o
 * modal veria o pedido desaparecer e teria de inferir sucesso a partir de ausência de
 * dado. Ver a spec de 14/08, §3.1.
 */
export function getProvisioningRequest(
  tenantId: number,
  requestId: number,
): Promise<ProvisioningRequest> {
  return api
    .get<ProvisioningRequest>(
      `/v1/tenants/${tenantId}/waf/provisioning-requests/${requestId}`,
    )
    .then(r => r.data)
}

/** Uma linha do histórico entre tenants. Vem de
 *  `GET /v1/admin/waf/provisioning-history`, que NÃO filtra status — é o `applied`
 *  que a lista por tenant esconde de propósito, e sem o qual um pedido
 *  bem-sucedido não aparecia em tela nenhuma. */
export interface ProvisioningHistoryItem {
  id: number
  kind: 'provision' | 'deletion'
  desired_name: string
  status: ProvisioningStatus | 'applied'
  tenant_id: number | null
  /** `null` quando o tenant não existe mais. A linha CONTINUA aparecendo: o JOIN é
   *  LEFT justamente para não perder o registro do que foi apagado. */
  tenant_name: string | null
  actor: number | null
  actor_email: string | null
  created_at: string
  applied_at: string | null
  /** Calculada no servidor: `applied_at − created_at` quando terminou, último passo
   *  menos criação quando falhou, `null` no resto. */
  duration_seconds: number | null
  error_detail: string | null
}

export function listProvisioningHistory(
  limit = 10,
): Promise<{ items: ProvisioningHistoryItem[] }> {
  return api
    .get<{ items: ProvisioningHistoryItem[] }>(
      `/v1/admin/waf/provisioning-history?limit=${limit}`,
    )
    .then(r => r.data)
}

/** Cria um pedido NOVO clonando o alvo do pedido informado. Não reabre a origem —
 *  o histórico fica imutável e cada tentativa vira uma linha, com a própria
 *  duração. A permissão cobrada é a da ação de ORIGEM: repetir uma exclusão exige
 *  `waf:delete`. */
export function retryProvisioningRequest(
  requestId: number,
): Promise<{ status: string; request_id: number; kind: string; tenant_id: number }> {
  return api
    .post(`/v1/admin/waf/provisioning-requests/${requestId}/retry`)
    .then(r => r.data)
}

/** Descarta um pedido TERMINAL: ele sai da lista de cartões e continua no
 *  histórico, com o `status` intacto.
 *
 *  Não é exclusão — nada some do banco. `failed` continua dizendo COMO o pedido
 *  terminou; `dismissed_at` só diz que ninguém precisa mais olhar. Até 26/08/2026
 *  não havia saída, e um `failed` de 10/08 seguia na tela com botão de repetir.
 *  A permissão é a mesma do retry, pelo `kind` da origem. */
export function dismissProvisioningRequest(
  requestId: number,
): Promise<{ status: string; request_id: number }> {
  return api
    .post(`/v1/admin/waf/provisioning-requests/${requestId}/dismiss`)
    .then(r => r.data)
}

export function provisionWaf(body: ProvisionWafBody): Promise<ProvisionWafResponse> {
  return api.post<ProvisionWafResponse>('/v1/waf/provision', body).then(r => r.data)
}

export function getWafRulesObservation(wafId: number, days = 7): Promise<WafRulesObservationResponse> {
  return api
    .get<WafRulesObservationResponse>(`/v1/waf/managed/${wafId}/rules/observation?days=${days}`)
    .then(r => r.data)
}

// ─── Tipos — Detalhe da regra ('Ver detalhes') ────────────────────────────────

export interface WafRuleDetailConfig {
  name: string
  priority: number
  kind: WafRuleKind
  effective_action: 'count' | 'active'
  metric: string | null
  statement: unknown
  host_scope: string | null
  managed: { vendor?: string; name?: string; version?: string | null } | null
  description?: string
}

export interface WafRuleSample {
  timestamp?: string
  client_ip?: string
  country?: string
  uri?: string
  method?: string
  action?: string
  rule?: string | null
}

export interface WafRuleDetail {
  rule: WafRuleDetailConfig
  sample_window_hours: number
  sampled: WafRuleSample[]
  sub_rule_breakdown: { sub_rule: string; count: number }[]
  sample_error?: string
  description_aws?: string
}

export interface WafLogTopItem {
  key: string | null
  hits: number
}

export interface WafRuleLogs {
  enabled: boolean
  reason?: string
  window_days?: number
  total?: number
  top_uris?: WafLogTopItem[]
  top_countries?: WafLogTopItem[]
  top_ips?: WafLogTopItem[]
  error?: string
}

export function getWafRuleDetail(wafId: number, ruleName: string): Promise<WafRuleDetail> {
  return api
    .get<WafRuleDetail>(`/v1/waf/managed/${wafId}/rules/${encodeURIComponent(ruleName)}`)
    .then((r) => r.data)
}

export function getWafRuleLogs(wafId: number, ruleName: string, days = 7): Promise<WafRuleLogs> {
  return api
    .get<WafRuleLogs>(`/v1/waf/managed/${wafId}/rules/${encodeURIComponent(ruleName)}/logs`, {
      params: { days },
    })
    .then((r) => r.data)
}

// ─── Tipos — Dashboard Executivo ──────────────────────────────────────────────

export interface WafKpi { value: number; delta_pct: number | null }

export interface WafDashboard {
  window: '24h' | '7d' | '30d'
  generated_at?: string
  logging_enabled: boolean
  errors?: string[]
  kpis?: {
    total: WafKpi
    blocked: WafKpi
    blocked_pct: number
    allowed: WafKpi
    countries: WafKpi
    ips: WafKpi
    /**
     * Por que os `delta_pct` vêm nulos, quando vêm. Ausente em resposta antiga —
     * a tela trata a ausência como "não sabemos", não como "há comparação".
     */
    comparison?: { available: boolean; reason: 'retention' | 'creation' | 'unreadable' | null }
  }
  timeseries?: { ts: string; allowed: number; blocked: number }[]
  block_types?: { rule: string; count: number }[]
  countries_access?: { country: string; count: number }[]
  countries_blocked?: { country: string; count: number }[]
  heatmap?: { dow: number; hour: number; count: number }[]
  top_uris?: { key: string; count: number }[]
  top_ips?: { key: string; count: number }[]
}

export function getWafDashboard(
  wafId: number,
  window: '24h' | '7d' | '30d' = '7d',
  host?: string,
): Promise<WafDashboard> {
  return api
    .get<WafDashboard>(`/v1/waf/managed/${wafId}/dashboard`, {
      params: { window, ...(host ? { host } : {}) },
    })
    .then((r) => r.data)
}

/**
 * Baixa o PDF do dashboard WAF via axios (que injeta o Bearer token) e dispara
 * o download no browser. Uma âncora nativa <a href> NÃO passa pelo interceptor
 * do axios, então iria sem o header Authorization → 401. Padrão espelhado de
 * finops.ts:exportOrgTreeServer.
 */
export async function getWafDashboardPdf(
  wafId: number,
  window: '24h' | '7d' | '30d',
): Promise<void> {
  const r = await api.get(`/v1/waf/managed/${wafId}/dashboard/pdf`, {
    params: { window },
    responseType: 'blob',
    timeout: 95000,
  })
  const cd = (r.headers?.['content-disposition'] as string | undefined) ?? ''
  const match = /filename="?([^"]+)"?/.exec(cd)
  const filename = match?.[1] ?? `waf-dashboard-${wafId}-${window}.pdf`
  const blob = r.data as Blob
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

export interface DeleteWafBody {
  /** Nome do Web ACL, digitado para confirmar. Comparado de forma EXATA. */
  confirm_name: string
  reason?: string | null
}

export interface DeleteWafResponse {
  status: 'pending' | 'pending_gmud'
  request_id: number
  gmud_id: number | null
  motivo_gate?: string
  sem_gmud_porque?: string
}

/**
 * Registra o pedido de exclusão de um WAF gerenciado.
 *
 * A rota NÃO chama a AWS — ela valida e grava o pedido; quem executa é o
 * scheduler, e o acompanhamento sai pelo mesmo endpoint de progresso do
 * provisionamento.
 */
export function deleteWaf(wafId: number, body: DeleteWafBody): Promise<DeleteWafResponse> {
  return api.delete<DeleteWafResponse>(`/v1/waf/managed/${wafId}`, { data: body }).then(r => r.data)
}

// ─── Modo código (TF+GIT) — spec 2026-10-14 ───────────────────────────────────

export interface Divergencia {
  campo: string
  no_codigo: unknown
  na_aws: unknown
  expressavel: boolean
}

export interface VinculoDeCodigo {
  stage_chave: string
  arquivo: string
  commit: string
  modules_sha: string
}

export interface ModoCodigo {
  vinculo: VinculoDeCodigo | null
  sem_vinculo: string | null
  campos: Record<string, { valor: unknown; origem: string; expressao: string | null }>
  divergencias: Divergencia[]
  pedido_ativo: PedidoNoCodigo | null
  /** Último pedido do stage que parou sem chamado (recusado antes do
   * Confirmar, vencido), só quando não há pedido ativo (`modo_codigo.pedido_parado`). */
  pedido_parado?: PedidoNoCodigo | null
  /** Os pedidos da TELA deste WAF que esperam sem ocupar o stage (spec
   * 2026-10-15 §5.2/D3): o livre guardado primeiro, depois a fila, com a posição. */
  pedidos_na_espera?: (PedidoNoCodigo & { posicao: number | null })[]
  emergencias?: EmergenciaForaDoCodigo[]
  /** O stage onde o WAF pode ser trazido para o código: só quando o vínculo é
   * `stage_sem_module_waf` (spec 2026-10-15 WAF 100% D10.4). */
  adocao?: { stage_chave: string } | null
}

/** Emergência que o código ainda não tem (`emergencia.fora_do_codigo`). */
export interface EmergenciaForaDoCodigo {
  id: number
  estado: 'aberta' | 'sem_pedido' | 'pedido_recusado'
  pedido_id: number | null
  gmud_id: number | null
  justificativa: string
  acao: AcaoDeWaf | null
  criada_em: string | null
}

/** O gesto do módulo waf (`waf_codigo/acoes.py`). */
export interface AcaoDeWaf {
  tipo: string
  grupo?: string | null
  sub_regra?: string | null
  valor?: unknown
}

/** O pedido que ocupa o stage, no resumo de `modo_codigo.pedido_ativo`. */
export interface PedidoNoCodigo {
  id: number
  estado: string
  origem: 'chat' | 'tela_waf' | 'emergencia_waf' | string
  waf_acao: (AcaoDeWaf & { managed_waf_id?: number }) | null
  requested_by_user_id: number | null
  mr_iid: number | null
  aprovado_no_plan: boolean | null
  motivo: string | null
  waf_reconciliado: string[]
  resumo: Record<string, number> | null
}

export interface PedidoCriado {
  pedido_id: number
  stage: string
  origem: string
  /** Spec 2026-10-15 §5.2: com o stage ocupado, pausado ou com fila, o pedido
   * nasce `na_fila`; a posição, quem ocupa e o motivo da pausa vêm junto. */
  estado?: string | null
  posicao?: number | null
  atras_de?: { id: number; estado: string } | null
  fila_pausada?: string | null
}

export function lerModoCodigo(id: number): Promise<ModoCodigo> {
  return api.get<ModoCodigo>(`/v1/waf/managed/${id}/codigo`).then(r => r.data)
}

export function criarPedidoDeCodigo(
  id: number,
  body: { acao: AcaoDeWaf; justificativa: string; divergencias_aceitas: Divergencia[] },
): Promise<PedidoCriado> {
  return api.post<PedidoCriado>(`/v1/waf/managed/${id}/pedidos`, body).then(r => r.data)
}

export function confirmarPedidoDeCodigo(id: number, pedidoId: number): Promise<unknown> {
  return api.post(`/v1/waf/managed/${id}/pedidos/${pedidoId}/confirmar`).then(r => r.data)
}

/** Abre o pedido que escreve o `waf.tf` do stage com `module "waf"` e os
 * `import` da ACL existente. O 422 traz `{erro: 'adocao_recusada', motivos}`. */
export function adotarNoCodigo(id: number, justificativa: string): Promise<PedidoCriado> {
  return api.post<PedidoCriado>(`/v1/waf/managed/${id}/codigo/adotar`, { justificativa })
    .then(r => r.data)
}

/** A emergência gravada (`waf_codigo/emergencia.aplicar`). */
export interface EmergenciaAplicada {
  emergencia_id: number
  pedido_id: number | null
  gmud_id: number | null
  motivo_gmud: string
  recusa_do_pedido: string | null
}

export function aplicarEmergencia(
  id: number, body: { acao: AcaoDeWaf; justificativa: string },
): Promise<EmergenciaAplicada> {
  return api.post<EmergenciaAplicada>(`/v1/waf/managed/${id}/emergencia`, body).then(r => r.data)
}

// ─── Lote de regra do catálogo (spec 2026-10-18, consoleai#25239) ─────────────

export interface RegraDoCatalogo {
  chave: string
  nome: string
  descricao: string
  casa: string[]
  nao_casa: string[]
}

export type SituacaoDoLote = 'aplicavel' | 'ja_tem' | 'codigo' | 'sem_permissao' | 'fora_de_escopo'

export interface ItemDaPrevia {
  waf_id: number
  nome: string | null
  tenant_id: number | null
  account_id: string | null
  region: string | null
  situacao: SituacaoDoLote
  motivo: string
}

export type EstadoDoItem =
  | 'pendente' | 'observando' | 'promovendo' | 'bloqueando'
  | 'aguardando_gmud' | 'ja_tem' | 'falhou'

export interface ItemDoLote {
  id: number
  managed_waf_id: number
  estado: EstadoDoItem
  nome: string
  tenant_id: number | null
  account_id: string
  gmud_id: number | null
  erro: string | null
  tentado_em: string | null
}

export interface LoteDeRegra {
  id: number
  regra: string
  motivo: string
  criado_em: string
  concluido_em: string | null
  itens: ItemDoLote[]
}

export interface ResumoDoLote {
  id: number
  regra: string
  motivo: string
  criado_em: string
  concluido_em: string | null
  total: number
  em_andamento: number
}

export function listarCatalogo(): Promise<RegraDoCatalogo[]> {
  return api.get<RegraDoCatalogo[]>('/v1/waf/catalogo').then(r => r.data)
}

export function previaDoLote(chave: string): Promise<{ itens: ItemDaPrevia[] }> {
  return api.post<{ itens: ItemDaPrevia[] }>(`/v1/waf/catalogo/${encodeURIComponent(chave)}/previa`)
    .then(r => r.data)
}

export function criarLote(chave: string, wafIds: number[], motivo: string): Promise<{ lote_id: number }> {
  return api.post<{ lote_id: number }>(`/v1/waf/catalogo/${encodeURIComponent(chave)}/lotes`,
    { waf_ids: wafIds, motivo }).then(r => r.data)
}

export function promoverLote(loteId: number, wafIds: number[], motivo: string): Promise<unknown> {
  return api.post(`/v1/waf/lotes/${loteId}/promover`, { waf_ids: wafIds, motivo }).then(r => r.data)
}

export function lerLote(loteId: number): Promise<LoteDeRegra> {
  return api.get<LoteDeRegra>(`/v1/waf/lotes/${loteId}`).then(r => r.data)
}

export function listarLotes(): Promise<ResumoDoLote[]> {
  return api.get<ResumoDoLote[]>('/v1/waf/lotes').then(r => r.data)
}
